// TeamsDestinations — the Microsoft account Strata posts to Teams as, and
// the picker that turns its teams / channels / chats into alert
// destinations.
//
// Connecting is a device-code sign-in: the backend starts it, we show the
// code + link, and poll until the admin has signed in as the shared account
// (strata@…) in another tab. The refresh token never touches the browser;
// it is sealed in the backend and rotated there.
//
// Sits inside <AlertsCard>; on its own here because the connect flow has
// enough state to deserve its own component.
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addAlertGraphChannel,
  disconnectTeams,
  getTeamsOverview,
  pollTeamsConnect,
  startTeamsConnect,
  type DeviceCodeStart,
} from "@/api/alerts";
import { I } from "@/components/Icons";
import { Pill } from "@/components/UI";
import { useUI } from "@/store/ui";
import { apiErrorMessage, extractErrorCode } from "@/utils/apiError";
import { formatDateTimeFull } from "@/utils/format";

const QK = ["admin-alerts-teams"];

export function TeamsDestinations({ onChanged }: { onChanged: () => void }) {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const { data, isLoading, error } = useQuery({
    queryKey: QK,
    queryFn: getTeamsOverview,
    retry: false,
  });
  const notConfigured =
    error != null && extractErrorCode(error) === "graph_not_configured";

  // Device-code state.
  const [dc, setDc] = useState<DeviceCodeStart | null>(null);
  const timer = useRef<number | null>(null);
  const stopPolling = () => {
    if (timer.current != null) window.clearInterval(timer.current);
    timer.current = null;
  };
  useEffect(() => stopPolling, []);

  const startMut = useMutation({
    mutationFn: startTeamsConnect,
    onSuccess: (start) => {
      setDc(start);
      stopPolling();
      timer.current = window.setInterval(
        async () => {
          try {
            const r = await pollTeamsConnect(start.id);
            if (r === "connected") {
              stopPolling();
              setDc(null);
              addToast({
                title: "Teams account connected",
                body: "Pick a channel or chat below.",
              });
              void qc.invalidateQueries({ queryKey: QK });
            }
          } catch (err) {
            stopPolling();
            setDc(null);
            addToast({
              title: "Sign-in failed",
              body: apiErrorMessage(err, "Try connecting again."),
              variant: "error",
            });
          }
        },
        Math.max(start.interval, 3) * 1000,
      );
    },
    onError: (err) =>
      addToast({
        title: "Could not start sign-in",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });
  const disconnectMut = useMutation({
    mutationFn: disconnectTeams,
    onSuccess: () => {
      addToast({ title: "Teams account disconnected" });
      void qc.invalidateQueries({ queryKey: QK });
    },
    onError: (err) =>
      addToast({
        title: "Could not disconnect",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });

  // Picker.
  const [pick, setPick] = useState("");
  const addMut = useMutation({
    mutationFn: ({
      name,
      teamId,
      channelId,
    }: {
      name: string;
      teamId: string;
      channelId: string;
    }) => addAlertGraphChannel(name, teamId, channelId),
    onSuccess: (row) => {
      setPick("");
      addToast({
        title: `Added ${row.name}`,
        body: "Alerts will be posted there as the Strata account.",
      });
      onChanged();
    },
    onError: (err) =>
      addToast({
        title: "Could not add destination",
        body: apiErrorMessage(err, ""),
        variant: "error",
      }),
  });
  const onPick = () => {
    if (!data || !pick) return;
    // Option values: "team|<teamId>|<channelId>|<label>" or "chat|<chatId>|<label>".
    const parts = pick.split("|");
    if (parts[0] === "team")
      addMut.mutate({ name: parts[3], teamId: parts[1], channelId: parts[2] });
    else addMut.mutate({ name: parts[2], teamId: "", channelId: parts[1] });
  };

  if (notConfigured) {
    return (
      <p className="dim" style={{ fontSize: 12 }}>
        Posting as the Strata account needs the Microsoft 365 connection
        (GRAPH_* on the server). Webhooks still work.
      </p>
    );
  }

  return (
    <div style={{ marginBottom: 10 }}>
      {isLoading && (
        <div className="dim" style={{ fontSize: 12 }}>
          Loading Microsoft account…
        </div>
      )}
      {error != null && !notConfigured && (
        <div className="dim" style={{ fontSize: 12, color: "var(--warn)" }}>
          {apiErrorMessage(error, "Could not load the Teams account.")}
        </div>
      )}

      {data && data.account == null && (
        <div
          className="row gap-8"
          style={{ alignItems: "center", flexWrap: "wrap" }}
        >
          <span className="dim" style={{ fontSize: 12 }}>
            No Microsoft account connected.
          </span>
          {dc == null ? (
            <button
              className="btn sm"
              type="button"
              onClick={() => startMut.mutate()}
              disabled={startMut.isPending}
            >
              <I.Plus size={11} /> Connect the Strata account
            </button>
          ) : (
            <span style={{ fontSize: 12 }}>
              Open{" "}
              <a href={dc.verification_url} target="_blank" rel="noreferrer">
                {dc.verification_url}
              </a>{" "}
              and enter <b className="mono">{dc.user_code}</b>, then sign in as
              the Strata account. Waiting…{" "}
              <button
                className="btn ghost sm"
                type="button"
                onClick={() => {
                  stopPolling();
                  setDc(null);
                }}
              >
                Cancel
              </button>
            </span>
          )}
        </div>
      )}

      {data && data.account != null && (
        <>
          <div
            className="row gap-8"
            style={{ alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}
          >
            <span style={{ fontSize: 12 }}>
              Posting as <b>{data.account.display_name || data.account.upn}</b>{" "}
              <span className="dim mono" style={{ fontSize: 11 }}>
                {data.account.upn}
              </span>
            </span>
            {data.account.last_error ? (
              <Pill tone="neg">needs reconnecting</Pill>
            ) : (
              <Pill tone="pos">connected</Pill>
            )}
            <span className="dim" style={{ fontSize: 11 }}>
              since {formatDateTimeFull(data.account.connected_at)}
            </span>
            <div className="spacer" />
            {data.account.last_error && dc == null && (
              <button
                className="btn sm"
                type="button"
                onClick={() => startMut.mutate()}
                disabled={startMut.isPending}
              >
                Reconnect
              </button>
            )}
            <button
              className="btn ghost sm"
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    "Disconnect the Strata Microsoft account? Channel and chat destinations stop delivering until it is reconnected.",
                  )
                )
                  disconnectMut.mutate();
              }}
              disabled={disconnectMut.isPending}
            >
              Disconnect
            </button>
          </div>
          {data.account.last_error && (
            <p
              className="dim"
              style={{ fontSize: 11, color: "var(--warn)", marginTop: 0 }}
            >
              {data.account.last_error}
            </p>
          )}
          {dc != null && (
            <p style={{ fontSize: 12 }}>
              Open{" "}
              <a href={dc.verification_url} target="_blank" rel="noreferrer">
                {dc.verification_url}
              </a>{" "}
              and enter <b className="mono">{dc.user_code}</b>. Waiting…
            </p>
          )}

          <div
            className="row gap-8"
            style={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <select
              className="input"
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              style={{ minWidth: 280 }}
              aria-label="Team channel or chat to add"
            >
              <option value="">Pick a channel or chat…</option>
              {data.teams.map((t) => (
                <optgroup key={t.id} label={t.name}>
                  {t.channels.map((ch) => (
                    <option
                      key={ch.id}
                      value={`team|${t.id}|${ch.id}|${t.name} › ${ch.name}`}
                    >
                      {ch.name}
                    </option>
                  ))}
                </optgroup>
              ))}
              {data.chats.length > 0 && (
                <optgroup label="Chats">
                  {data.chats.map((c) => (
                    <option key={c.id} value={`chat|${c.id}|${c.name} (chat)`}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <button
              className="btn accent sm"
              type="button"
              onClick={onPick}
              disabled={!pick || addMut.isPending}
            >
              <I.Plus size={11} /> Add destination
            </button>
            {data.teams.length === 0 && data.chats.length === 0 && (
              <span className="dim" style={{ fontSize: 11 }}>
                The account is not in any team or chat yet — add it to one in
                Teams first.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
